import { React, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box, CircularProgress, Backdrop, Button, Typography,
    DialogContent, List, ListItem, ListItemText,
    IconButton, Paper, Dialog, DialogTitle, DialogActions, DialogContentText,
    FormControl, InputLabel, Select, MenuItem, TextField
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { toast } from 'react-toastify';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { socket } from './socket';

var api = axios.create({ withCredentials: true });

function Dashboards(props) {
    const { getAccessTokenSilently } = useAuth0();
    const [dashboards, setDashboards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableauViews, setTableauViews] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openRenameDialog, setOpenRenameDialog] = useState(false);
    const [selectedView, setSelectedView] = useState('');
    const [dashboardTitle, setDashboardTitle] = useState('');
    const [dashboardToDelete, setDashboardToDelete] = useState(null);
    const [dashboardToRename, setDashboardToRename] = useState(null);
    const [newDashboardTitle, setNewDashboardTitle] = useState('');
    const [addLoading, setAddLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [renameLoading, setRenameLoading] = useState(false);
    const user = props.user;
    const isConnected = props.isConnected;

    // Function to get all dashboards
    function getDashboards() {
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {                api.get(`${process.env.REACT_APP_BASEURL}/api/dashboards/all`, { 
                headers: { Authorization: `Bearer ${token}` } 
            }).then(response => {
                // Make sure dashboards are sorted by sortorder
                const sortedDashboards = response.data.sort((a, b) => a.sortorder - b.sortorder);
                setDashboards(sortedDashboards);
                setLoading(false);
            }).catch((error) => {
                console.log(error.response ? error.response.data : error);
                toast.error('Failed to load dashboards');
                setLoading(false);
            });
        }).catch((token_error) => {
            console.log(token_error);
            setLoading(false);
        });
    }

    // Function to save the new dashboard order
    function saveDashboardOrder() {
        setLoading(true);
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.post(
                `${process.env.REACT_APP_BASEURL}/api/dashboards/reorder`,
                { dashboards },
                { headers: { Authorization: `Bearer ${token}` } }
            ).then((response) => {
                toast.success('Dashboard order updated successfully');
                // Emit an event to notify all connected clients about the change
                socket.emit('dashboards', true);
                setLoading(false);
            }).catch((error) => {
                console.log(error.response ? error.response.data : error);
                toast.error('Failed to update dashboard order');
                setLoading(false);
            });
        }).catch((token_error) => {
            console.log(token_error);
            setLoading(false);
        });
    }

    // Handle drag end event
    const onDragEnd = (result) => {
        if (!result.destination) return;
        
        const items = Array.from(dashboards);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        // Update the sortorder for each item based on its new index
        const updatedItems = items.map((item, index) => ({
            ...item,
            sortorder: index
        }));
        
        setDashboards(updatedItems);
    };

    // Function to fetch Tableau views
    function getTableauViews() {
        setAddLoading(true);
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.get(`${process.env.REACT_APP_BASEURL}/api/tableau/views`, { 
                headers: { Authorization: `Bearer ${token}` } 
            }).then(response => {
                setTableauViews(response.data);
                setAddLoading(false);
            }).catch((error) => {
                console.log(error.response ? error.response.data : error);
                toast.error('Failed to load Tableau views');
                setAddLoading(false);
            });
        }).catch((token_error) => {
            console.log(token_error);
            setAddLoading(false);
        });
    }

    // Function to add a new dashboard
    function addDashboard() {
        setAddLoading(true);
        // Find the selected view object
        const view = tableauViews.find(v => v.id === selectedView);
        
        if (!view) {
            toast.error('Please select a valid view');
            setAddLoading(false);
            return;
        }
        
        if (!dashboardTitle.trim()) {
            toast.error('Please enter a dashboard title');
            setAddLoading(false);
            return;
        }

        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.post(`${process.env.REACT_APP_BASEURL}/api/dashboards/add`, {
                title: dashboardTitle,
                viewId: view.id,
                contentUrl: view.contentUrl
            }, { 
                headers: { Authorization: `Bearer ${token}` } 
            }).then(response => {
                toast.success('Dashboard added successfully');
                setOpenAddDialog(false);
                getDashboards();
                // Reset form
                setDashboardTitle('');
                setSelectedView('');
                setAddLoading(false);
            }).catch((error) => {
                console.log(error.response ? error.response.data : error);
                toast.error(error.response && error.response.data || 'Failed to add dashboard');
                setAddLoading(false);
            });
        }).catch((token_error) => {
            console.log(token_error);
            setAddLoading(false);
        });
    }

    // Handle opening the add dashboard dialog
    const handleOpenAddDialog = () => {
        setOpenAddDialog(true);
        getTableauViews();
    };

    // Handle closing the add dashboard dialog
    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
        setDashboardTitle('');
        setSelectedView('');
    };

    // Handle opening delete confirmation dialog
    const handleOpenDeleteDialog = (dashboard) => {
        setDashboardToDelete(dashboard);
        setOpenDeleteDialog(true);
    };

    // Handle closing delete confirmation dialog
    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setDashboardToDelete(null);
    };

    // Handle opening rename dialog
    const handleOpenRenameDialog = (dashboard) => {
        setDashboardToRename(dashboard);
        setNewDashboardTitle(dashboard.title);
        setOpenRenameDialog(true);
    };

    // Handle closing rename dialog
    const handleCloseRenameDialog = () => {
        setOpenRenameDialog(false);
        setDashboardToRename(null);
        setNewDashboardTitle('');
    };

    // Function to delete a dashboard
    function deleteDashboard() {
        if (!dashboardToDelete) return;
        
        setDeleteLoading(true);
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.delete(`${process.env.REACT_APP_BASEURL}/api/dashboards/delete/${dashboardToDelete._id}`, { 
                headers: { Authorization: `Bearer ${token}` } 
            }).then(response => {
                toast.success('Dashboard deleted successfully');
                handleCloseDeleteDialog();
                getDashboards();
                // Emit an event to notify all connected clients about the change
                socket.emit('dashboards', true);
            }).catch((error) => {
                console.log(error.response ? error.response.data : error);
                toast.error(error.response && error.response.data || 'Failed to delete dashboard');
            }).finally(() => {
                setDeleteLoading(false);
            });
        }).catch((token_error) => {
            console.log(token_error);
            setDeleteLoading(false);
        });
    }

    // Function to rename a dashboard
    function renameDashboard() {
        if (!dashboardToRename) return;
        
        if (!newDashboardTitle.trim()) {
            toast.error('Please enter a dashboard title');
            return;
        }
        
        setRenameLoading(true);
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.put(`${process.env.REACT_APP_BASEURL}/api/dashboards/rename/${dashboardToRename._id}`, {
                title: newDashboardTitle
            }, { 
                headers: { Authorization: `Bearer ${token}` } 
            }).then(response => {
                toast.success('Dashboard renamed successfully');
                handleCloseRenameDialog();
                getDashboards();
                // Emit an event to notify all connected clients about the change
                socket.emit('dashboards', true);
            }).catch((error) => {
                console.log(error.response ? error.response.data : error);
                toast.error(error.response && error.response.data || 'Failed to rename dashboard');
            }).finally(() => {
                setRenameLoading(false);
            });
        }).catch((token_error) => {
            console.log(token_error);
            setRenameLoading(false);
        });
    }

    useEffect(() => {
        if (isConnected) {
            getDashboards();
            socket.on('dashboards', () => { getDashboards(); });
        }

        return () => {
            socket.off('dashboards');
        };
    }, [isConnected]);

    if (user.admin) {
        if (loading) {
            return (
                <Box
                    component="main"
                    sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
                >
                    <CircularProgress size={60} thickness={4} />
                </Box>
            );
        } else {
            return (
                <>
                    <Backdrop
                        sx={{ color: '#fff', zIndex: 10000000 }}
                        open={false}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                    <DialogContent sx={{ 
                        p: 0, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        height: 'calc(100vh - 64px)' // Adjust based on AppBar height
                    }}>
                        {/* Fixed Header Section */}
                        <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                Drag and drop dashboards to change their display order. The order determines how dashboards are displayed to users throughout the application. Changes will be applied to all users after clicking "Save Order".
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                                ℹ️ Tip: Grab the handle on the left side of each dashboard to drag it to a new position.
                            </Typography>
                        </Box>
                        
                        {/* Scrollable List Section */}
                        <Box sx={{ 
                            flexGrow: 1, 
                            overflow: 'auto',
                            px: 3,
                            py: 2
                        }}>
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="dashboards">
                                    {(provided) => (
                                        <List {...provided.droppableProps} ref={provided.innerRef} sx={{ p: 0 }}>
                                            {dashboards.map((dashboard, index) => (
                                                <Draggable key={dashboard._id} draggableId={dashboard._id} index={index}>
                                                    {(provided) => (
                                                        <ListItem
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            sx={{
                                                                border: '1px solid #e0e0e0',
                                                                borderRadius: '4px',
                                                                mb: 1,
                                                                bgcolor: 'background.paper',
                                                                '&:hover': {
                                                                    bgcolor: '#f5f5f5',
                                                                },
                                                                transition: 'background-color 0.2s ease'
                                                            }}
                                                        >
                                                            <div
                                                                {...provided.dragHandleProps}
                                                                style={{ cursor: 'grab', display: 'flex', alignItems: 'center', marginRight: '8px' }}
                                                            >
                                                                <DragIndicatorIcon color="action" />
                                                            </div>
                                                            <ListItemText 
                                                                primary={dashboard.title} 
                                                                secondary={
                                                                    <>
                                                                        <Typography component="span" variant="body2" color="text.primary">
                                                                            Dashboard: {dashboard.dashboard}
                                                                        </Typography>
                                                                        <br />
                                                                        <Typography component="span" variant="body2" color="text.secondary">
                                                                            Current Order: {dashboard.sortorder}
                                                                        </Typography>
                                                                    </>
                                                                }
                                                                sx={{ flex: 1 }}
                                                            />
                                                            <IconButton 
                                                                edge="end" 
                                                                aria-label="delete" 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenDeleteDialog(dashboard);
                                                                }}
                                                                sx={{ 
                                                                    color: 'error.main',
                                                                    '&:hover': { 
                                                                        backgroundColor: 'error.lighter' 
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteOutlineIcon />
                                                            </IconButton>
                                                            <IconButton 
                                                                edge="end" 
                                                                aria-label="rename" 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenRenameDialog(dashboard);
                                                                }}
                                                                sx={{ 
                                                                    color: 'primary.main',
                                                                    '&:hover': { 
                                                                        backgroundColor: 'primary.lighter' 
                                                                    },
                                                                    ml: 1
                                                                }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </ListItem>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </List>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </Box>
                        
                        {/* Fixed Footer Section with Buttons */}
                        <Box sx={{ 
                            p: 2, 
                            borderTop: '1px solid #e0e0e0',
                            display: 'flex', 
                            justifyContent: 'space-between',
                            bgcolor: 'background.paper'
                        }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button 
                                    variant="outlined" 
                                    color="secondary" 
                                    onClick={getDashboards}
                                    disabled={loading}
                                    startIcon={<RefreshIcon />}
                                >
                                    Reset Order
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    color="primary" 
                                    onClick={handleOpenAddDialog}
                                    disabled={loading}
                                    startIcon={<AddCircleOutlineIcon />}
                                >
                                    Add Dashboard
                                </Button>
                            </Box>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={saveDashboardOrder}
                                disabled={loading}
                            >
                                Save Order
                            </Button>
                        </Box>
                    </DialogContent>

                    {/* Add Dashboard Dialog */}
                    <Dialog 
                        open={openAddDialog} 
                        onClose={handleCloseAddDialog}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Add New Dashboard</DialogTitle>
                        <DialogContent sx={{ pt: 1 }}>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="dashboard-title"
                                label="Dashboard Title"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={dashboardTitle}
                                onChange={(e) => setDashboardTitle(e.target.value)}
                                disabled={addLoading}
                                sx={{ mb: 3 }}
                            />
                            {tableauViews.length === 0 && !addLoading ? (
                                <Box sx={{ 
                                    p: 2, 
                                    textAlign: 'center', 
                                    border: '1px dashed #ccc', 
                                    borderRadius: 1,
                                    bgcolor: '#f9f9f9',
                                    mb: 2
                                }}>
                                    <Typography variant="body1" color="text.secondary">
                                        All available Tableau views have already been added as dashboards.
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Delete an existing dashboard if you want to re-add it with a different configuration.
                                    </Typography>
                                </Box>
                            ) : null}
                            
                            <FormControl fullWidth variant="outlined" disabled={addLoading || tableauViews.length === 0}>
                                <InputLabel id="tableau-view-select-label">Tableau View</InputLabel>
                                <Select
                                    labelId="tableau-view-select-label"
                                    id="tableau-view-select"
                                    value={selectedView}
                                    onChange={(e) => setSelectedView(e.target.value)}
                                    label="Tableau View"
                                >
                                    {addLoading ? (
                                        <MenuItem value="">
                                            <em>Loading views...</em>
                                        </MenuItem>
                                    ) : tableauViews.length === 0 ? (
                                        <MenuItem value="">
                                            <em>No new views available</em>
                                        </MenuItem>
                                    ) : (
                                        tableauViews.map((view) => (
                                            <MenuItem key={view.id} value={view.id}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="body1">{view.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                        {view.contentUrl}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            {selectedView && (
                                <Box sx={{ mt: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f9f9f9' }}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        Selected View Information
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                        {tableauViews.find(v => v.id === selectedView)?.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                                        {tableauViews.find(v => v.id === selectedView)?.contentUrl}
                                    </Typography>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={handleCloseAddDialog} disabled={addLoading}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={addDashboard} 
                                variant="contained" 
                                color="primary"
                                disabled={addLoading || !selectedView || !dashboardTitle.trim()}
                            >
                                {addLoading ? 'Adding...' : 'Add Dashboard'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Delete Dashboard Confirmation Dialog */}
                    <Dialog
                        open={openDeleteDialog}
                        onClose={handleCloseDeleteDialog}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">
                            {"Confirm Dashboard Deletion"}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Are you sure you want to delete the dashboard "{dashboardToDelete?.title}"? 
                                This action cannot be undone and will remove this dashboard from all user views.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button 
                                onClick={handleCloseDeleteDialog} 
                                color="primary" 
                                disabled={deleteLoading}
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={deleteDashboard} 
                                color="error" 
                                variant="contained"
                                disabled={deleteLoading}
                                startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteOutlineIcon />}
                                autoFocus
                            >
                                {deleteLoading ? "Deleting..." : "Delete Dashboard"}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Rename Dashboard Dialog */}
                    <Dialog
                        open={openRenameDialog}
                        onClose={handleCloseRenameDialog}
                        aria-labelledby="rename-dialog-title"
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle id="rename-dialog-title">Rename Dashboard</DialogTitle>
                        <DialogContent sx={{ pt: 1 }}>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="new-dashboard-title"
                                label="New Dashboard Title"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={newDashboardTitle}
                                onChange={(e) => setNewDashboardTitle(e.target.value)}
                                disabled={renameLoading}
                                sx={{ mb: 2 }}
                            />
                            <DialogContentText>
                                Renaming the dashboard will change its title for all users. Please choose a new title for the dashboard.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={handleCloseRenameDialog} disabled={renameLoading}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={renameDashboard} 
                                variant="contained" 
                                color="primary"
                                disabled={renameLoading || !newDashboardTitle.trim()}
                            >
                                {renameLoading ? 'Renaming...' : 'Rename Dashboard'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            );
        }
    } else {
        return (
            <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
            >
                <WarningRoundedIcon size={60} thickness={4} />
                <p>Not Authorized</p>
            </Box>
        );
    }
}

export default Dashboards;
